import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type {
  FilterationType,
  FiltersContextType,
  UpdateFilterType,
} from "./FiltersContextType";
import { useTabsContext } from "../tabsContext";
import type { ListType } from "../../shared/api/ApiType";
import { ALL_AVAILABLE_FILTERS } from "../../entities/entities";
import { useCommonPropertiesContext } from "../commonPropertiesContext";
import type { KeyOptionsType } from "../../components/masterComponents/Filters/FiltersType";

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);

  if (!context)
    throw new Error("useFiltersContext must be used inside FiltersProvider");
  return context;
};

export const FiltersProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [appliedFilters, setAppliedFilters] = useState<ListType[]>([]);

  const [ignoredFilter, setIgnoredFilter] = useState<Record<string, boolean>>(
    {},
  );

  const { valuesByDefault } = useTabsContext();
  const { classes } = useCommonPropertiesContext();

  const sourceArray = ["llm", "finals", "validated"];

  const DEFAULT_FILTRATION_TYPE: Record<KeyOptionsType, FilterationType> = {
    "": "equal",
    description: "equal",
    class_name: "equal",
    item: "equal",
    value: "include",
    tag: "equal",
    source: "equal",
  };

  const allAvailableFilters = useMemo(
    () =>
      ALL_AVAILABLE_FILTERS.map((f) => ({
        ...f,
        ...(f.key === "class_name" && { filterOptions: classes }),
        ...(f.key === "source" && { filterOptions: sourceArray }),
        defaultFilterType: DEFAULT_FILTRATION_TYPE[f.key],
      })),
    [classes],
  );

  useEffect(() => {
    const className = appliedFilters.find((f) => f.key === "class_name");
    const length = className?.values.length || 0;

    if (valuesByDefault.length === 0) return;
    if (length < 2) {
      const isClassNameCheck = ignoredFilter[className?.key ?? ""] ?? true;
      if (isClassNameCheck) {
        updateFilter("class_name", valuesByDefault);
      }
    }
  }, [valuesByDefault]);

  useEffect(() => {
    updateFilter("source", [sourceArray[0]]);
  }, []);

  const updateFilter: UpdateFilterType = (key, values, filtration_type) => {
    setAppliedFilters((prev) => {
      const existing = prev.find((f) => f.key === key);

      if (values.length === 0) {
        return prev.filter((f) => f.key !== key);
      }

      const fltrType =
        filtration_type ??
        existing?.filtration_type ??
        DEFAULT_FILTRATION_TYPE[key];

      if (existing)
        return prev.map((f) =>
          f.key === key ? { ...f, values, filtration_type: fltrType } : f,
        );

      return [...prev, { key, values, filtration_type: fltrType }];
    });
  };

  return (
    <FiltersContext.Provider
      value={{
        appliedFilters,
        setAppliedFilters,
        ignoredFilter,
        setIgnoredFilter,
        allAvailableFilters,
        updateFilter,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
